/**
 * PhysicsDiagnosticPanelContent — kit tool `physicsDiag`. Kernel/physics/input
 * checkpoint readouts (kernel heartbeat, PhysicsSystem TPS, gravity, character
 * velocity/position, movement-allowed, grounded). Zero-prop: player comes from
 * useGame() (same source the HUD fed it before the kit migration); the 🛠 kit's
 * ToolWindow provides the window chrome, so this renders the body directly.
 */

import React from 'react';
import { GameConstants } from '../../../shared/GameConstants';
import { useGame } from '../contexts/GameContext';
import { DiagnosticCheckpointBody, DiagnosticCheckpoint } from '../components/DiagnosticCheckpointBody';
import { livePlayerPosition } from '../../systems/player/livePlayerPosition';

export const PhysicsDiagnosticPanelContent: React.FC = () => {
  const { player } = useGame();

  const getCheckpoints = (): DiagnosticCheckpoint[] => {
    return [
      {
        label: 'Kernel Loop Status',
        category: 'physics',
        check: () => {
          try {
            const scene = (window as any).__currentScene;
            const kernel = scene?.setup?.kernel;
            if (kernel) {
              const heartbeat = kernel.getHeartbeat();
              if (heartbeat === -1) return 'Not Booted';
              return heartbeat < 100 ? true : `FROZEN (${Math.round(heartbeat)}ms since last tick)`;
            }
            return 'Kernel not found';
          } catch { return 'Error'; }
        },
      },
      {
        label: 'Physics Engine Status',
        category: 'physics',
        check: () => {
          try {
            const scene = (window as any).__currentScene;
            const physSystem = scene?.setup?.kernel?.getSystem?.('PhysicsSystem');
            if (physSystem) {
              const ticks = (physSystem as any).tickCount || 0;
              const tps = (physSystem as any).currentTps || 0;
              return `Running (Ticks: ${ticks}, TPS: ${tps.toFixed(1)})`;
            }
            return 'PhysicsSystem not loaded';
          } catch { return 'Error'; }
        },
      },
      {
        label: 'Global Gravity (Y)',
        category: 'physics',
        check: () => {
          try {
            return `Y: ${GameConstants.Physics.GRAVITY}`;
          } catch { return false; }
        },
      },
      {
        label: 'Character Velocity',
        category: 'physics',
        check: () => {
          if (player && player.velocity) {
            return `V(${player.velocity.x.toFixed(2)}, ${player.velocity.y.toFixed(2)})`;
          }
          return 'No velocity data on player state';
        },
      },
      {
        label: 'Character Position',
        category: 'physics',
        check: () => {
          if (player && player.position) {
            const at = livePlayerPosition(player.position);
            return `(${Math.round(at.x)}, ${Math.round(at.y)})`;
          }
          return 'No position data found';
        },
      },
      {
        label: 'Movement Allowed',
        category: 'input',
        check: () => {
          try {
            const scene = (window as any).__currentScene;
            const inputSystem = scene?.setup?.kernel?.getSystem?.('InputSystem');
            if (inputSystem) {
              const isBlocked = (inputSystem as any).isBlocked;
              return !isBlocked ? true : 'NO (Input prevented)';
            }
            return 'Unknown (No InputSystem)';
          } catch { return false; }
        },
      },
      {
        label: 'Grounded Check',
        category: 'input',
        check: () => {
          try {
            const scene = (window as any).__currentScene;
            const physSystem = scene?.setup?.kernel?.getSystem?.('PhysicsSystem');
            if (physSystem && player) {
              const entitySystem = scene?.setup?.kernel?.getSystem?.('EntitySystem');
              const localPlayer = entitySystem?.getPlayerManager?.()?.getPlayer?.();
              if (localPlayer) {
                return localPlayer.isGrounded ? 'YES (Grounded)' : 'NO (Airborne)';
              }
              return player.isGrounded ? 'YES (Grounded)' : 'NO (Airborne)';
            }
            return 'Physics/Entity System missing';
          } catch { return false; }
        }
      }
    ];
  };

  const categories = [
    { id: 'physics', label: '🍎 Physics / Kinematics' },
    { id: 'input', label: '🎮 Input / State Checks' }
  ];

  return (
    <DiagnosticCheckpointBody
      categories={categories}
      checkpoints={getCheckpoints()}
      pollIntervalMs={200}
    />
  );
};
